/**
 * Store reviews page — two Klaviyo mounts (summary card + list), same pattern as legacy Prestige.
 * After hydration, filter chevrons are swapped for DS icons; native media-checkbox SVGs are removed (CSS ::before).
 */

const LIST_HOST_SELECTOR = '[data-dev-reviews-page-host]';
const SUMMARY_HOST_SELECTOR = '[data-dev-reviews-summary-host]';
const FILTER_CHEVRON_TEMPLATE_ID = 'dev-reviews-page-filter-chevron';
const SEARCH_ICON_TEMPLATE_ID = 'dev-reviews-page-search-icon';
const PRODUCT_CHEVRON_TEMPLATE_ID = 'dev-reviews-page-product-chevron';
const DS_CHEVRON_ATTR = 'data-dev-ds-chevron';
const DS_SEARCH_ATTR = 'data-dev-ds-search';

/** @param {Element} root */
function fitFilterSelectWidths(root) {
  const selects = root.querySelectorAll('.kl_reviews__filters__pill select');

  for (const select of selects) {
    if (!(select instanceof HTMLSelectElement)) continue;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) continue;

    const measurer = document.createElement('span');
    measurer.textContent = selectedOption.text;
    measurer.setAttribute('aria-hidden', 'true');
    measurer.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;white-space:nowrap;height:0;overflow:hidden;';

    const styles = getComputedStyle(select);
    measurer.style.fontFamily = styles.fontFamily;
    measurer.style.fontSize = styles.fontSize;
    measurer.style.fontWeight = styles.fontWeight;
    measurer.style.letterSpacing = styles.letterSpacing;

    select.parentElement?.appendChild(measurer);
    select.style.width = `${measurer.offsetWidth}px`;
    measurer.remove();
  }
}

/** @param {Element} root */
function bindFilterSelectResize(root) {
  if (root.dataset.devReviewsFilterResizeBound === 'true') return;

  root.dataset.devReviewsFilterResizeBound = 'true';

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.closest('.kl_reviews__filters__pill')) return;
    fitFilterSelectWidths(root);
  });
}

/** @param {Element} root */
function normalizeStarSizes(root) {
  const stars = root.querySelectorAll(
    '.kl_reviews__stars_badge svg, .kl_reviews__full_star, .kl_reviews__partial_star, .kl_reviews__empty_star'
  );

  for (const star of stars) {
    if (!(star instanceof SVGElement)) continue;

    star.style.removeProperty('width');
    star.style.removeProperty('height');
    star.setAttribute('width', star.closest('.summary-reviews-container') ? '16' : '12');
    star.setAttribute('height', star.closest('.summary-reviews-container') ? '16' : '12');
  }
}

/** @param {Element} root */
function replaceSearchIcon(root) {
  const template = document.getElementById(SEARCH_ICON_TEMPLATE_ID);
  if (!(template instanceof HTMLTemplateElement)) return;

  const iconHost = root.querySelector('.kl_reviews__search_icon');
  if (!(iconHost instanceof HTMLElement)) return;

  const nativeSvg = iconHost.querySelector('svg:not([' + DS_SEARCH_ATTR + '])');
  if (!(nativeSvg instanceof SVGElement)) return;

  const clone = template.content.cloneNode(true);
  const dsSvg = clone.querySelector('svg');
  if (!(dsSvg instanceof SVGElement)) return;

  dsSvg.setAttribute(DS_SEARCH_ATTR, '');
  nativeSvg.replaceWith(dsSvg);
}

/** @param {Element} root */
function replaceFilterChevrons(root) {
  const template = document.getElementById(FILTER_CHEVRON_TEMPLATE_ID);
  if (!(template instanceof HTMLTemplateElement)) return;

  const pills = root.querySelectorAll('.kl_reviews__filters__pill:has(select)');

  for (const pill of pills) {
    const nativeSvg = pill.querySelector('svg');
    if (!(nativeSvg instanceof SVGElement) || nativeSvg.hasAttribute(DS_CHEVRON_ATTR)) {
      continue;
    }

    const clone = template.content.cloneNode(true);
    const dsSvg = clone.querySelector('svg');
    if (!(dsSvg instanceof SVGElement)) continue;

    dsSvg.setAttribute(DS_CHEVRON_ATTR, '');
    nativeSvg.replaceWith(dsSvg);
  }
}

/** @param {Element} root */
function replaceProductCardChevrons(root) {
  const template = document.getElementById(PRODUCT_CHEVRON_TEMPLATE_ID);
  if (!(template instanceof HTMLTemplateElement)) return;

  const carats = root.querySelectorAll('.kl_reviews__review__product_card__carat');

  for (const carat of carats) {
    const nativeSvg = carat.querySelector('svg:not([' + DS_CHEVRON_ATTR + '])');
    if (!(nativeSvg instanceof SVGElement)) continue;

    const clone = template.content.cloneNode(true);
    const dsSvg = clone.querySelector('svg');
    if (!(dsSvg instanceof SVGElement)) continue;

    dsSvg.setAttribute(DS_CHEVRON_ATTR, '');
    nativeSvg.replaceWith(dsSvg);
  }
}

/** @param {Element} root */
function removeMediaFilterNativeIcons(root) {
  const buttons = root.querySelectorAll(
    '.kl_reviews__filter_with_left_align_icon button, .kl_reviews__filters__pill button[role="checkbox"]'
  );

  for (const button of buttons) {
    if (!(button instanceof HTMLButtonElement)) continue;

    for (const svg of button.querySelectorAll('svg')) {
      svg.remove();
    }
  }
}

/** @returns {boolean} */
function ensureSummaryMount() {
  const summaryHost = document.querySelector(SUMMARY_HOST_SELECTOR);
  const listHost = document.querySelector(LIST_HOST_SELECTOR);

  if (!(summaryHost instanceof HTMLElement) || !(listHost instanceof HTMLElement)) {
    return false;
  }

  if (summaryHost.querySelector('.kl_reviews__summary')) {
    return true;
  }

  const listWrapper = listHost.querySelector('#klaviyo-all-reviews-wrapper');
  const summary = listWrapper?.querySelector('.kl_reviews__summary');

  if (!(summary instanceof HTMLElement)) {
    return false;
  }

  const summaryMount = summaryHost.querySelector('.dev-reviews-page__summary-mount');
  if (!(summaryMount instanceof HTMLElement)) {
    return false;
  }

  summaryMount.appendChild(summary);
  return true;
}

/** @returns {boolean} */
function summaryHostIsReady() {
  const summaryHost = document.querySelector(SUMMARY_HOST_SELECTOR);
  if (!(summaryHost instanceof HTMLElement)) return true;

  return Boolean(summaryHost.querySelector('.kl_reviews__summary'));
}

/** @param {Element} root */
function iconsNeedPatch(root) {
  const needsSearch = root.querySelector('.kl_reviews__search_icon svg:not([' + DS_SEARCH_ATTR + '])');
  const needsChevron = root.querySelector(
    '.kl_reviews__filters__pill:has(select) svg:not([' + DS_CHEVRON_ATTR + '])'
  );
  const needsProductChevron = root.querySelector(
    '.kl_reviews__review__product_card__carat svg:not([' + DS_CHEVRON_ATTR + '])'
  );
  const needsMediaIcon = root.querySelector(
    '.kl_reviews__filter_with_left_align_icon button svg, .kl_reviews__filters__pill button[role="checkbox"] svg'
  );

  return Boolean(needsSearch || needsChevron || needsProductChevron || needsMediaIcon);
}

/**
 * @param {Element} root
 * @param {{ fitWidths?: boolean }} [options]
 */
function patchKlaviyoUi(root, { fitWidths = false } = {}) {
  const wrapper = root.querySelector('#klaviyo-all-reviews-wrapper');
  if (!wrapper) return false;

  replaceSearchIcon(root);
  replaceFilterChevrons(root);
  replaceProductCardChevrons(root);
  removeMediaFilterNativeIcons(root);
  normalizeStarSizes(root);
  ensureSummaryMount();

  const summaryHost = document.querySelector(SUMMARY_HOST_SELECTOR);
  if (summaryHost instanceof HTMLElement) {
    normalizeStarSizes(summaryHost);
  }

  if (fitWidths) {
    fitFilterSelectWidths(root);
  }

  return true;
}

/** @param {Element} root */
function watchKlaviyoUiPatch(root) {
  let filtersFitted = false;
  let frameScheduled = false;

  const runPatch = () => {
    frameScheduled = false;

    const shouldFitWidths = !filtersFitted && Boolean(root.querySelector('.kl_reviews__filters__pill select'));
    if (!patchKlaviyoUi(root, { fitWidths: shouldFitWidths })) return;

    if (shouldFitWidths) {
      filtersFitted = true;
    }

    if (!iconsNeedPatch(root) && summaryHostIsReady()) {
      observer.disconnect();
    }
  };

  const schedulePatch = () => {
    if (frameScheduled) return;
    frameScheduled = true;
    requestAnimationFrame(runPatch);
  };

  const observer = new MutationObserver(schedulePatch);

  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-checked'] });

  const summaryHost = document.querySelector(SUMMARY_HOST_SELECTOR);
  if (summaryHost instanceof HTMLElement) {
    observer.observe(summaryHost, { childList: true, subtree: true });
  }

  window.setTimeout(() => {
    observer.disconnect();
  }, 15000);

  schedulePatch();
}

/** @param {Element} root */
function bindFilterIconRefresh(root) {
  root.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const mediaButton = target.closest(
        '.kl_reviews__filter_with_left_align_icon button, .kl_reviews__filters__pill button[role="checkbox"]'
      );
      if (!mediaButton) return;

      requestAnimationFrame(() => {
        removeMediaFilterNativeIcons(root);
        requestAnimationFrame(() => {
          removeMediaFilterNativeIcons(root);
        });
      });
    },
    true
  );
}

/** @param {Element} root */
function initReviewsPage(root) {
  bindFilterSelectResize(root);
  bindFilterIconRefresh(root);
  watchKlaviyoUiPatch(root);
}

const listHost = document.querySelector(LIST_HOST_SELECTOR);
if (listHost instanceof HTMLElement) {
  initReviewsPage(listHost);
}
